/**
 * The one Save of the customer Settings page: the dirty parts, as an ordered
 * list of writes to their owners (`runSettingsWrites` says why the order
 * matters and why nothing spans them).
 *
 * 1. Credit control: the payer's payment settings. A customer with no billing
 *    account creates one at the view's `create.url`; one with an account
 *    updates it at the account's `paymentSettings` link. Never a URL built
 *    here: the adapter posts where the read said. A customer with no account
 *    whose only edits are invoicing settings still creates it (with the
 *    methods it has, card only), as the settings belong to a billing account.
 * 2. The sales ledger: only the changed invoicing settings, with the same
 *    reason, at the URL the read carried. When step 1 has just created the
 *    billing account the read had no URL yet, so the view is read again for
 *    it first (order to invoice doc 50 §9).
 *
 * A refused write keeps every edit, shows the error with the fields
 * (field-tagged errors bound to their inputs, api-validation.md) and sends
 * nothing after it. Anything saved reads the credit view again, which tells
 * the header chip.
 */
import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CustomerCreditView } from "@shared-core/data/credit-control-types";
import {
  extractFieldErrors,
  type FieldError,
} from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  runSettingsWrites,
  saveFailureMessage,
  type SettingsWrite,
} from "@shared-core/domain/settings-save";
import type { InvoicingDraft, PaymentDraft } from "./useSettingsDrafts";

/** What a card shows after a refused save: the message and the fields it names. */
export interface PaymentSaveError {
  message: string;
  fieldErrors: FieldError[];
}

export const PAYMENT_PART = "Payment methods";
export const INVOICING_PART = "Invoicing settings";

export function useSaveSettings({
  view,
  payment,
  invoicing,
  reloadCredit,
}: {
  view: CustomerCreditView;
  payment: PaymentDraft;
  invoicing: InvoicingDraft;
  reloadCredit: () => Promise<void>;
}) {
  const adapter = useDataAdapter();
  const [saving, setSaving] = useState(false);
  const [paymentError, setPaymentError] = useState<PaymentSaveError | null>(
    null,
  );
  const [invoicingError, setInvoicingError] = useState<PaymentSaveError | null>(
    null,
  );
  const [barMessage, setBarMessage] = useState<string | null>(null);

  const clearMessages = () => {
    setPaymentError(null);
    setInvoicingError(null);
    setBarMessage(null);
  };

  const writesPayment =
    payment.dirty || (invoicing.dirty && !view.billingAccount);

  const writes = (): SettingsWrite[] => [
    ...(writesPayment
      ? [
          {
            part: PAYMENT_PART,
            write: async (reason: string) => {
              if (view.billingAccount) {
                await adapter.updateBillingAccountPayment(view.billingAccount, {
                  ...payment.values,
                  reason,
                });
              } else {
                await adapter.createBillingAccount(view, {
                  ...payment.values,
                  reason,
                });
              }
            },
          },
        ]
      : []),
    ...(invoicing.dirty && invoicing.invoicing
      ? [
          {
            part: INVOICING_PART,
            write: async (reason: string) => {
              let target = invoicing.invoicing!;
              if (!target.url) {
                // The billing account was created by the write before this one.
                const reread = await adapter.getCustomerCredit(view.customerId);
                if (!reread.invoicing?.url) {
                  throw new Error(
                    "The billing account was created, but its invoicing settings could not be read back. Reload the page and save them again.",
                  );
                }
                target = reread.invoicing;
              }
              await adapter.updateInvoicingSettings(target, {
                changes: invoicing.changes,
                reason,
              });
            },
          },
        ]
      : []),
  ];

  const save = async (reason: string) => {
    setSaving(true);
    clearMessages();
    try {
      const outcome = await runSettingsWrites(writes(), reason);
      if (outcome.saved.length > 0) await reloadCredit();
      if (outcome.kind === "failed") {
        const message = mutationErrorMessage(
          outcome.error,
          `Could not save the ${outcome.failed.toLowerCase()}.`,
        );
        const refused = {
          message,
          fieldErrors: extractFieldErrors(outcome.error),
        };
        if (outcome.failed === PAYMENT_PART) setPaymentError(refused);
        if (outcome.failed === INVOICING_PART) setInvoicingError(refused);
        setBarMessage(saveFailureMessage(outcome, message));
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    paymentError,
    invoicingError,
    barMessage,
    save,
    clearMessages,
  };
}
